<script lang="ts" setup>
import { Heading, Text } from '~ds/components/ui/typography';

/**
 * An index page's title: the page name set beside its 3D illustration, the page's h1 at every size,
 * with an optional subtitle under the name. `visuallyHidden` keeps the title for screen readers only,
 * for a page the site navigation already names, and leaves the illustration out so it isn't fetched.
 */
interface Props {
  title: string;
  illustrationSrc?: string;
  subtitle?: string;
  visuallyHidden?: boolean;
}

const props = withDefaults(defineProps<Props>(), { illustrationSrc: undefined, subtitle: undefined, visuallyHidden: false });

const rootClasses = computed(() => twMerge(cva('', {
  variants: {
    visuallyHidden: {
      true: 'sr-only',
      false: 'flex items-center gap-3',
    },
  },
})({ visuallyHidden: props.visuallyHidden })));
</script>

<template>
  <div
    :class="rootClasses"
    data-testid="index-header"
  >
    <NuxtImg
      v-if="illustrationSrc && !visuallyHidden"
      :src="illustrationSrc"
      alt=""
      role="presentation"
      width="80"
      height="80"
      fit="inside"
      class="size-15 shrink-0 object-contain md:size-20"
    />
    <div class="flex flex-col gap-2">
      <Heading as="h1" size="xl">
        {{ title }}
      </Heading>
      <Text
        v-if="subtitle"
        size="sm"
        data-testid="index-header-subtitle"
      >
        {{ subtitle }}
      </Text>
    </div>
  </div>
</template>
